/**
 * Mode clair ou sombre de la console : celui du système au premier passage,
 * puis le choix de l'admin, retenu dans ce navigateur. Fournit le thème MUI
 * et le bouton de bascule de l'en-tête.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { PaletteMode } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { logger } from '../lib/logger';
import { creerTheme } from './theme';

/** Clé du choix retenu dans le navigateur. */
const CLE_MODE = 'suklu-admin-mode';

interface ModeAffichageValeur {
  mode: PaletteMode;
  basculer: () => void;
}

const ModeAffichageContext = createContext<ModeAffichageValeur>({ mode: 'light', basculer: () => undefined });

/** Mode retenu, sinon celui du système ; le stockage peut être refusé (navigation privée). */
const lireModeInitial = (): PaletteMode => {
  try {
    const retenu = window.localStorage.getItem(CLE_MODE);
    if (retenu === 'light' || retenu === 'dark') return retenu;
  } catch (erreur) {
    logger.warn('[ModeAffichage][lireModeInitial] stockage illisible, mode du système', { erreur: String(erreur) });
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/** Retient le choix ; un refus du stockage n'empêche pas la bascule. */
const retenirMode = (mode: PaletteMode) => {
  try {
    window.localStorage.setItem(CLE_MODE, mode);
  } catch (erreur) {
    logger.warn('[ModeAffichage][retenirMode] choix non retenu', { erreur: String(erreur) });
  }
};

/** Fournit le thème du mode courant à toute la console. */
export const ModeAffichageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(lireModeInitial);
  const basculer = useCallback(() => setMode((actuel) => {
    const suivant = actuel === 'light' ? 'dark' : 'light';
    retenirMode(suivant);
    return suivant;
  }), []);
  const theme = useMemo(() => creerTheme(mode), [mode]);
  const valeur = useMemo(() => ({ mode, basculer }), [mode, basculer]);
  return (
    <ModeAffichageContext.Provider value={valeur}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ModeAffichageContext.Provider>
  );
};

/** Mode courant et bascule. */
export const useModeAffichage = (): ModeAffichageValeur => useContext(ModeAffichageContext);
